import { useEffect, useRef, useState, type RefObject } from 'react';
import { SCROLL_LOCK_ATTRIBUTE } from './useScrollLock';
import { DIRECTION_LOCK_PX, EDGE_GUARD_PX } from './gestureSlop';

/**
 * Pull-to-refresh on handheld (ADR 0048, plan 15 §5.4, chunk H).
 *
 * The document is the scroller on a phone, and `overscroll-behavior-y:
 * contain` on `body` (base.css) is what stops Chrome's own pull-to-reload —
 * which would reload the whole app — from firing over the lists. This is the
 * gesture that replaces it: the same pull, refreshing the list instead of the
 * page.
 *
 * ## Why touch events, not pointer events
 *
 * A vertical drag is the browser's (`touch-action: pan-y` on the rows, the
 * default everywhere else), and the moment the browser takes a pan it sends
 * `pointercancel` and no more pointer events. Touch events keep coming for
 * the whole of the touch, whoever scrolls, so they are the only way to follow
 * a finger that the browser also thinks is a scroll.
 *
 * ## Who owns the gesture
 *
 * A pull starts only on a touch that *begins* with the document at the very
 * top (`scrollY === 0`) — a drag that scrolls the list back up to its top is
 * a scroll that bottoms out, never a pull — on the given container or on the
 * empty page around it (an element that *contains* the container: the space
 * under a short list is the shell's, not the list's), with no overlay open (the drawer and sheets lock the document), clear of the
 * left-edge back gesture, and with one finger.
 *
 * The decision is made once, on the first 8 px of movement, by the rule
 * `useSwipeActions` uses on the rows, mirrored: moving *down more than
 * sideways* is a pull; `|dx| > |dy|` is the swipe's, and it stands down on
 * everything else. So one touch can never be both, and a tie is neither.
 * Moving up first is a scroll. Nothing is cancelled before the decision, so a
 * finger that jitters on its way to a scroll is never stuck; after it, each
 * `touchmove` is cancelled (when the browser still allows it) so the page
 * does not rubber-band under the indicator.
 *
 * ## Refresh
 *
 * Released past {@link PULL_THRESHOLD_PX}, `onRefresh` runs once and the
 * state stays `refreshing` until the promise it returns settles, succeed or
 * fail; pulls meanwhile are ignored. Released short, it springs back.
 *
 * The gesture has a non-gesture twin wherever it is used — the list header's
 * Refresh button on mail, the ⋮ sheet's Refresh on contacts.
 */

/** Finger travel past which a release refreshes. */
export const PULL_THRESHOLD_PX = 64;
/** How far the indicator follows the finger before it stops. */
export const PULL_MAX_PX = 112;


export type PullPhase = 'idle' | 'pulling' | 'armed' | 'refreshing';

export interface PullState {
  phase: PullPhase;
  /** How far the indicator is pulled down, 0 … PULL_MAX_PX. */
  distance: number;
}

export interface PullToRefreshOptions {
  /** Off on desktop, and wherever the list is not on screen. Default on. */
  enabled?: boolean | undefined;
}

const IDLE: PullState = { phase: 'idle', distance: 0 };

interface Gesture {
  id: number;
  startX: number;
  startY: number;
  phase: 'undecided' | 'pulling';
  distance: number;
}

interface TouchPoint {
  identifier: number;
  clientX: number;
  clientY: number;
}

function findTouch(list: ArrayLike<TouchPoint>, id: number): TouchPoint | undefined {
  for (let i = 0; i < list.length; i += 1) {
    const touch = list[i];
    if (touch?.identifier === id) return touch;
  }
  return undefined;
}

export function usePullToRefresh(
  containerRef: RefObject<HTMLElement | null>,
  onRefresh: () => unknown,
  options: PullToRefreshOptions = {},
): PullState {
  const enabled = options.enabled ?? true;
  const [state, setState] = useState<PullState>(IDLE);

  // The latest callback, read on release — the listeners below are bound
  // once, not re-bound on every render of the list.
  const latest = useRef(onRefresh);
  useEffect(() => {
    latest.current = onRefresh;
  });

  /** True from the release that refreshes until the refresh settles. */
  const refreshing = useRef(false);

  useEffect(() => {
    if (!enabled) return;

    let gesture: Gesture | undefined;
    let mounted = true;

    const abandon = () => {
      if (gesture?.phase === 'pulling') setState(IDLE);
      gesture = undefined;
    };

    const onTouchStart = (event: TouchEvent) => {
      // A second finger is a pinch, not a pull.
      if (gesture) {
        abandon();
        return;
      }
      if (refreshing.current || event.touches.length !== 1) return;
      if (window.scrollY > 0) return;
      if (document.documentElement.hasAttribute(SCROLL_LOCK_ATTRIBUTE)) return;
      const touch = event.touches[0];
      if (!touch || touch.clientX <= EDGE_GUARD_PX) return;
      const container = containerRef.current;
      const target = event.target;
      if (!container || !(target instanceof Node)) return;
      // On the list, or on the bare page around it (below a short list, say);
      // not on anything beside it — the app bar, the bottom bar, the FAB.
      if (!container.contains(target) && !target.contains(container)) return;
      gesture = {
        id: touch.identifier,
        startX: touch.clientX,
        startY: touch.clientY,
        phase: 'undecided',
        distance: 0,
      };
    };

    const onTouchMove = (event: TouchEvent) => {
      const g = gesture;
      if (!g) return;
      const touch = findTouch(event.touches, g.id);
      if (!touch) return;
      const dx = touch.clientX - g.startX;
      const dy = touch.clientY - g.startY;

      if (g.phase === 'undecided') {
        if (Math.max(Math.abs(dx), Math.abs(dy)) < DIRECTION_LOCK_PX) return;
        // Up first (a scroll), sideways (the swipe's), or the page moved
        // under the finger after all. Either way this touch is not ours.
        if (dy <= Math.abs(dx) || window.scrollY > 0) {
          gesture = undefined;
          return;
        }
        g.phase = 'pulling';
      }

      if (event.cancelable) event.preventDefault();
      const distance = Math.min(Math.max(dy, 0), PULL_MAX_PX);
      if (distance === g.distance) return;
      g.distance = distance;
      setState({ phase: dy >= PULL_THRESHOLD_PX ? 'armed' : 'pulling', distance });
    };

    const onTouchEnd = (event: TouchEvent) => {
      const g = gesture;
      if (!g || !findTouch(event.changedTouches, g.id)) return;
      gesture = undefined;
      if (g.phase !== 'pulling') return;
      if (g.distance < PULL_THRESHOLD_PX) {
        setState(IDLE);
        return;
      }
      refresh();
    };

    const onTouchCancel = () => abandon();

    function refresh() {
      refreshing.current = true;
      setState({ phase: 'refreshing', distance: PULL_THRESHOLD_PX });
      let result: unknown;
      try {
        result = latest.current();
      } catch {
        result = undefined;
      }
      void Promise.resolve(result)
        .catch(() => undefined)
        .then(() => {
          refreshing.current = false;
          if (mounted) setState(IDLE);
        });
    }

    document.addEventListener('touchstart', onTouchStart, { passive: true });
    // Not passive: a pull cancels the moves it owns (see the header).
    document.addEventListener('touchmove', onTouchMove, { passive: false });
    document.addEventListener('touchend', onTouchEnd);
    document.addEventListener('touchcancel', onTouchCancel);

    return () => {
      mounted = false;
      document.removeEventListener('touchstart', onTouchStart);
      document.removeEventListener('touchmove', onTouchMove);
      document.removeEventListener('touchend', onTouchEnd);
      document.removeEventListener('touchcancel', onTouchCancel);
      gesture = undefined;
      refreshing.current = false;
      setState(IDLE);
    };
  }, [containerRef, enabled]);

  return enabled ? state : IDLE;
}
